import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, RefreshCw, DollarSign, CreditCard, User, ChevronLeft, ChevronRight, CheckCircle, XCircle, TrendingUp, Users, BarChart3, Download, FileText, FileSpreadsheet, Gift } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { API_URL } from '../../config/api';
import jspdf from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ROWS_PER_PAGE = 10;

const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getPresetRange = (preset) => {
    const now = new Date();
    const today = toYMD(now);
    switch (preset) {
        case 'today': return { start: today, end: today };
        case 'week': { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); return { start: toYMD(s), end: today }; }
        case 'month': return { start: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), end: today };
        case 'quarter': { const q = Math.floor(now.getMonth() / 3) * 3; return { start: toYMD(new Date(now.getFullYear(), q, 1)), end: today }; }
        case 'year': return { start: toYMD(new Date(now.getFullYear(), 0, 1)), end: today };
        default: return { start: today, end: today };
    }
};

const fmt = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

const PAYMENT_METHOD_LABELS = {
    efectivo: 'Efectivo',
    tarjeta_debito: 'Tarjeta Débito',
    tarjeta_credito: 'Tarjeta Crédito',
    transferencia: 'Transferencia',
    cheque: 'Cheque',
    tarjeta: 'Tarjeta'
};

const getPaymentLabel = (m) => PAYMENT_METHOD_LABELS[m] || m || '-';

const SkeletonConsultations = () => (
    <>
        <div className="skeleton-grid">
            {[1, 2].map(i => <div className="skeleton-card" key={i}><div className="skeleton-icon" /><div className="skeleton-text"><div className="skeleton-line short" /><div className="skeleton-line tall" /></div></div>)}
        </div>
        <div className="charts-row"><div className="skeleton-chart" style={{ height: '300px' }}><div className="skeleton-inner" /></div></div>
    </>
);

const BillingConsultationsReport = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [activePreset, setActivePreset] = useState('month');
    const [dateRange, setDateRange] = useState(() => getPresetRange('month'));
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ data: [], summary: { total_consultations: 0, total_exempt: 0, by_doctor: {} } });
    const [exportOpen, setExportOpen] = useState(false);

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/reports/billing-consultations?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!res.ok) throw new Error('Error al cargar reporte de consultas');
            const result = await res.json();
            setData(result);
            setPage(1);
        } catch (err) { setError(err.message); } finally { setLoading(false); setRefreshing(false); }
    }, [dateRange]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleExportPDF = () => {
        const doc = new jspdf('l', 'pt', 'a4');
        doc.setFontSize(18);
        doc.text("Reporte de Cobranza - Consultas", 40, 40);
        doc.setFontSize(11);
        doc.text(`Periodo: ${dateRange.start} a ${dateRange.end}`, 40, 60);

        const tableData = data.data.map(item => [
            item.paciente || '-',
            item.medico || '-',
            getPaymentLabel(item.metodo_pago),
            fmt(item.costo_consulta),
            fmt(item.iva_consulta),
            fmt(item.total_con_iva),
            item.exempt_count > 0 ? 'Sí' : 'No',
            item.solicita_factura == 1 ? 'Sí' : 'No',
            item.fecha ? new Date(item.fecha).toLocaleDateString() : 'N/D'
        ]);

        autoTable(doc, {
            startY: 80,
            head: [['Paciente', 'Médico', 'Método', 'Neto', 'IVA', 'Total', 'Exento', 'Factura', 'Fecha']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [8, 54, 119] }
        });

        doc.save(`Reporte_Consultas_${dateRange.start}_${dateRange.end}.pdf`);
        setExportOpen(false);
    };

    const handleExportExcel = () => {
        const exportData = data.data.map(item => ({
            'Paciente': item.paciente || '-',
            'Médico': item.medico || '-',
            'Método Pago': getPaymentLabel(item.metodo_pago),
            'Monto Neto': item.costo_consulta || 0,
            'IVA': item.iva_consulta || 0,
            'Total': item.total_con_iva || 0,
            'Exento': item.exempt_count > 0 ? 'Sí' : 'No',
            'Factura': item.solicita_factura == 1 ? 'Sí' : 'No',
            'Fecha': item.fecha || 'N/D'
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Consultas");
        XLSX.writeFile(wb, `Reporte_Consultas_${dateRange.start}_${dateRange.end}.xlsx`);
        setExportOpen(false);
    };

    const handlePreset = (p) => { setActivePreset(p); setDateRange(getPresetRange(p)); };
    const handleDateChange = (e) => { setActivePreset(''); setDateRange(prev => ({ ...prev, [e.target.name]: e.target.value })); };

    const totalPages = Math.max(1, Math.ceil(data.data.length / ROWS_PER_PAGE));
    const paginatedItems = useMemo(() => {
        const start = (page - 1) * ROWS_PER_PAGE;
        return data.data.slice(start, start + ROWS_PER_PAGE);
    }, [data.data, page]);

    const chartData = useMemo(() => {
        return Object.entries(data.summary.by_doctor)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [data.summary.by_doctor]);

    const COLORS = ['#083677', '#00BBBF', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

    return (
        <div className="report-view reports-hub-content custom-scrollbar">
            {/* Header */}
            <div className="report-header-actions">
                <div className="report-title-wrapper">
                    <div className="report-title">
                        <BarChart3 size={28} />
                        <h2>Cobranza de Consultas</h2>
                    </div>
                    <p>Detalle de facturación de consultas por médico</p>
                </div>
                <div className="report-controls">
                    <div className="date-range-picker">
                        <Calendar size={16} />
                        <input type="date" name="start" value={dateRange.start} onChange={handleDateChange} className="date-input" />
                        <span>a</span>
                        <input type="date" name="end" value={dateRange.end} onChange={handleDateChange} className="date-input" />
                    </div>
                    <button className={`btn-icon ${refreshing ? 'spinning' : ''}`} onClick={() => fetchData(true)} disabled={loading || refreshing} title="Actualizar">
                        <RefreshCw size={18} />
                    </button>
                    <div className="export-menu-container" style={{ position: 'relative' }}>
                        <button className="btn-secondary" onClick={() => setExportOpen(!exportOpen)} title="Exportar">
                            <Download size={18} />
                            <span>Exportar</span>
                        </button>
                        {exportOpen && (
                            <div className="export-dropdown" style={{ 
                                position: 'absolute', top: '100%', right: 0, 
                                background: 'white', border: '1px solid #e2e8f0', 
                                borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 1000, minWidth: '160px', marginTop: '8px',
                                animation: 'dropdown-in 0.2s ease-out', overflow: 'hidden'
                            }}>
                                <button onClick={handleExportPDF} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileText size={16} color="#ef4444" />
                                    Exportar PDF
                                </button>
                                <button onClick={handleExportExcel} className="export-item" style={{ 
                                    width: '100%', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                                    borderTop: '1px solid #f1f5f9', fontSize: '0.875rem', color: '#1e293b'
                                }}>
                                    <FileSpreadsheet size={16} color="#10b981" />
                                    Exportar Excel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Presets */}
            <div className="date-presets" style={{ marginBottom: '1.5rem' }}>
                {[
                    { key: 'today', label: 'Hoy' },
                    { key: 'week', label: 'Semana' },
                    { key: 'month', label: 'Mes' },
                    { key: 'quarter', label: 'Trimestre' },
                    { key: 'year', label: 'Año' }
                ].map(p => (
                    <button
                        key={p.key}
                        className={`preset-chip ${activePreset === p.key ? 'active' : ''}`}
                        onClick={() => handlePreset(p.key)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {error ? (
                <div className="error-banner">{error}</div>
            ) : loading ? (
                <SkeletonConsultations />
            ) : (
                <>
                    <div className="report-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                        <div className="kpi-card highlight">
                            <div className="kpi-icon-wrapper patients">
                                <Users size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Total de Consultas</span>
                                <span className="kpi-value">{data.summary.total_consultations}</span>
                            </div>
                        </div>
                        <div className="kpi-card" style={{ borderLeft: '4px solid #10b981' }}>
                            <div className="kpi-icon-wrapper" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                                <Gift size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Consultas Exentas</span>
                                <span className="kpi-value" style={{ color: '#10b981' }}>{data.summary.total_exempt || 0}</span>
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper income">
                                <TrendingUp size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Monto Neto</span>
                                <span className="kpi-value">{fmt(data.summary.total_neto || 0)}</span>
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                                <DollarSign size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">IVA Recaudado</span>
                                <span className="kpi-value" style={{ color: '#3b82f6' }}>{fmt(data.summary.total_iva || 0)}</span>
                            </div>
                        </div>
                        <div className="kpi-card highlight-income" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <div className="kpi-icon-wrapper" style={{ backgroundColor: '#083677', color: 'white' }}>
                                <BarChart3 size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Ingreso Total</span>
                                <span className="kpi-value" style={{ color: '#083677' }}>{fmt(data.summary.total_general || 0)}</span>
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="kpi-icon-wrapper ticket">
                                <DollarSign size={24} />
                            </div>
                            <div className="kpi-details">
                                <span className="kpi-title">Ticket Promedio</span>
                                <span className="kpi-value">
                                    {fmt(data.summary.total_consultations > 0 
                                        ? data.summary.total_general / data.summary.total_consultations 
                                        : 0)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="charts-row" style={{ gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: '1.5rem' }}>
                        <div className="chart-container">
                            <h3 className="chart-title"><TrendingUp size={18} /> Resumen por Médico</h3>
                            <div style={{ width: '100%', height: 300, marginTop: '1rem' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis 
                                            dataKey="name" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fill: '#64748b', fontSize: 12 }}
                                            interval={0}
                                        />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                        <Tooltip 
                                            cursor={{ fill: 'rgba(8, 54, 119, 0.05)' }}
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                                        />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={40}>
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="chart-container">
                            <h3 className="chart-title"><Users size={18} /> Ranking de Médicos</h3>
                            <div className="custom-scrollbar" style={{ marginTop: '1rem', flex: 1, overflowY: 'auto', paddingRight: '0.5rem' }}>
                                {chartData.map((doc, index) => (
                                    <div key={doc.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.85rem 1rem', background: index === 0 ? 'rgba(8, 54, 119, 0.04)' : 'transparent', borderRadius: '12px', marginBottom: '0.5rem', border: '1px solid', borderColor: index === 0 ? 'rgba(8, 54, 119, 0.1)' : 'transparent' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                            <span style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: index < 3 ? COLORS[index] : '#f1f5f9', color: index < 3 ? '#fff' : '#64748b', borderRadius: '50%', fontSize: '0.75rem', fontWeight: 'bold' }}>{index + 1}</span>
                                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{doc.name}</span>
                                        </div>
                                        <span style={{ fontWeight: '700', color: COLORS[index % COLORS.length], fontSize: '0.95rem' }}>{doc.value}</span>
                                    </div>
                                ))}
                                {chartData.length === 0 && <p className="text-muted text-center" style={{ marginTop: '2rem' }}>No hay datos disponibles</p>}
                            </div>
                        </div>
                    </div>

                    <div className="table-card">
                        <div className="table-header-flex">
                            <h3><CreditCard size={18} /> Detalle de Consultas</h3>
                            <span className="results-count">{data.data.length} resultados</span>
                        </div>
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Folio / Fecha</th>
                                        <th>Paciente</th>
                                        <th>Médico</th>
                                        <th>Exento</th>
                                        <th>Factura</th>
                                        <th>Método</th>
                                        <th style={{ textAlign: 'right' }}>Neto</th>
                                        <th style={{ textAlign: 'right' }}>IVA</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedItems.length > 0 ? paginatedItems.map((c, i) => (
                                        <tr key={i}>
                                            <td>
                                                <strong>{c.folio}</strong><br />
                                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>{new Date(c.fecha).toLocaleDateString()}</span>
                                            </td>
                                            <td>{c.paciente}</td>
                                            <td>{c.medico}</td>
                                            <td>
                                                <span style={{ 
                                                    padding: '2px 8px', 
                                                    borderRadius: '12px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: 'bold',
                                                    backgroundColor: c.exempt_count > 0 ? '#fef3c7' : '#f1f5f9',
                                                    color: c.exempt_count > 0 ? '#92400e' : '#64748b'
                                                }}>
                                                    {c.exempt_count > 0 ? 'SÍ' : 'NO'}
                                                </span>
                                            </td>
                                            <td>
                                                {c.solicita_factura == 1 ? (
                                                    <span style={{
                                                        display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '500',
                                                        backgroundColor: c.factura_entregada == 1 ? '#dcfce7' : '#fef9c3',
                                                        color: c.factura_entregada == 1 ? '#166534' : '#854d0e',
                                                        border: `1px solid ${c.factura_entregada == 1 ? '#bbf7d0' : '#fef08a'}`
                                                    }}>
                                                        {c.factura_entregada == 1 ? 'Facturado' : 'Pendiente'}
                                                    </span>
                                                ) : <span className="text-muted">No solicitada</span>}
                                            </td>
                                            <td>
                                                <span className="payment-badge">{getPaymentLabel(c.metodo_pago)}</span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{fmt(c.costo_consulta)}</td>
                                            <td style={{ textAlign: 'right', color: '#64748b' }}>{fmt(c.iva_consulta)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmt(c.total_con_iva)}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="9" className="text-center text-muted" style={{ padding: '2rem' }}>No hay datos para este periodo</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft size={16} /></button>
                                <span>Página {page} de {totalPages}</span>
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight size={16} /></button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default BillingConsultationsReport;
